/**
 * Combien facturer : du revenu qu'on veut garder au tarif d'une journée.
 *
 * Le calculateur de revenu net part d'un chiffre d'affaires et descend jusqu'à
 * ce qu'il reste. Ce module fait le chemin inverse, le seul qui réponde à la
 * question qu'on se pose en fixant un prix : combien faut-il encaisser pour
 * garder tant ?
 *
 * IL NE RECALCULE RIEN. Il cherche, par dichotomie, le chiffre d'affaires que
 * `revenuNet` transforme en l'objectif : cotisations, contribution à la
 * formation et impôt du foyer viennent donc exactement du calculateur de revenu
 * net, et les deux outils ne peuvent pas se contredire.
 *
 * TROIS CHOSES QUE LES CALCULATEURS DE TARIF OUBLIENT
 *
 * 1. Les frais se paient sur ce qui reste. En micro, rien ne se déduit :
 *    l'abattement forfaitaire tient lieu de frais pour l'impôt, et les
 *    cotisations se calculent sur tout le chiffre d'affaires. Chaque euro de
 *    frais doit donc être encaissé EN PLUS de ce qu'on veut garder, et passer
 *    au passage par les cotisations et l'impôt.
 *
 * 2. Une année ne compte pas cinquante-deux semaines facturées. Les jours
 *    fériés qui tombent en semaine changent d'une année à l'autre (neuf en
 *    2026, sept en 2027), et personne d'autre que le tarif ne paie les
 *    vacances.
 *
 * 3. Le plafond du régime borne ce que la micro peut rapporter. Au-delà, ses
 *    taux ne s'appliquent plus : le module le signale, et le parcours refuse
 *    d'afficher un tarif qui supposerait d'en sortir.
 *
 * Les valeurs viennent du référentiel arbitré
 * (docs/glossaries/micro-entreprise/reference-2026.json), comme celles de
 * revenuNet.
 */

import { revenuNet, PLAFONDS, SEUILS_TVA } from "./microEnterprise.js";

/**
 * Les activités qui se facturent au temps passé. Le prix d'une marchandise se
 * construit sur son coût d'achat et sa marge, pas sur des journées : la vente
 * n'a pas de tarif journalier.
 */
export const ACTIVITES_AU_TEMPS = ["services", "bnc", "cipav"];

const JOUR_MS = 86400000;

/**
 * Dimanche de Pâques du calendrier grégorien, en UTC.
 *
 * Algorithme dit « anonyme » (Meeus, Jones, Butcher), exact pour toute année
 * grégorienne. Trois fêtes légales en dépendent : le lundi de Pâques,
 * l'Ascension et le lundi de Pentecôte.
 */
export function paques(annee) {
    const a = annee % 19;
    const b = Math.floor(annee / 100);
    const c = annee % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const n = h + l - 7 * m + 114;
    return new Date(Date.UTC(annee, Math.floor(n / 31) - 1, (n % 31) + 1));
}

/**
 * Les onze fêtes légales de l'article L. 3133-1 du code du travail, à dates
 * DISTINCTES.
 *
 * Deux fêtes peuvent tomber le même jour : en 2008, l'Ascension était le
 * 1er mai. Les compter deux fois retirerait un jour qui n'a jamais été chômé.
 *
 * L'Alsace et la Moselle en ajoutent deux (Vendredi saint et 26 décembre), qui
 * relèvent du droit local et ne sont pas retenues ici.
 */
export function joursFeries(annee) {
    const p = paques(annee).getTime();
    const fixe = (mois, jour) => new Date(Date.UTC(annee, mois - 1, jour));
    const dates = [
        fixe(1, 1), // Jour de l'an
        new Date(p + JOUR_MS), // Lundi de Pâques
        fixe(5, 1), // Fête du travail
        fixe(5, 8), // Victoire 1945
        new Date(p + 39 * JOUR_MS), // Ascension
        new Date(p + 50 * JOUR_MS), // Lundi de Pentecôte
        fixe(7, 14), // Fête nationale
        fixe(8, 15), // Assomption
        fixe(11, 1), // Toussaint
        fixe(11, 11), // Armistice
        fixe(12, 25), // Noël
    ];
    const vues = new Set();
    return dates.filter((date) => {
        const t = date.getTime();
        if (vues.has(t)) return false;
        vues.add(t);
        return true;
    });
}

const enSemaine = (date) => date.getUTCDay() >= 1 && date.getUTCDay() <= 5;

/** Les fêtes légales qui tombent du lundi au vendredi, les seules qui coûtent un jour. */
export function joursFeriesEnSemaine(annee) {
    return joursFeries(annee).filter(enSemaine).length;
}

/** Lundis à vendredis de l'année civile. */
export function joursOuvres(annee) {
    let n = 0;
    for (
        let date = new Date(Date.UTC(annee, 0, 1));
        date.getUTCFullYear() === annee;
        date = new Date(date.getTime() + JOUR_MS)
    ) {
        if (enSemaine(date)) n++;
    }
    return n;
}

/**
 * Jours facturés dans l'année, arrondis au jour inférieur.
 *
 * Les jours fériés se retirent au prorata des jours travaillés : à quatre jours
 * par semaine, on suppose qu'un férié sur cinq tombe le jour où l'on ne
 * travaille pas. C'est une moyenne, pas un calendrier, puisque le jour chômé de
 * chacun n'est pas connu. À cinq jours, le compte est exact.
 */
export function joursFactures({ joursParSemaine, semainesConges, annee }) {
    const disponibles = joursOuvres(annee) - joursFeriesEnSemaine(annee);
    const jours = (disponibles * joursParSemaine) / 5 - semainesConges * joursParSemaine;
    // La marge absorbe l'erreur d'arrondi d'un produit décimal : 181,6 doit
    // donner 181, et 227 ne doit jamais devenir 226,9999.
    return Math.max(0, Math.floor(jours + 1e-9));
}

/**
 * Le plus petit chiffre d'affaires, à l'euro, que `revenuNet` transforme en au
 * moins `reste`.
 *
 * La dichotomie tient parce que le net croît avec le chiffre d'affaires : aucun
 * prélèvement n'en prend cent pour cent. La décote de l'impôt fait exception
 * sur quelques centimes à son seuil ; le résultat reste alors le premier euro
 * qui atteint l'objectif en venant d'en dessous, ce qui est la seule réponse
 * utile pour fixer un prix.
 */
export function chiffreAffairesPour(reste, parametres) {
    if (reste <= 0) return 0;
    const net = (chiffreAffaires) => revenuNet({ ...parametres, chiffreAffaires }).net;

    let bas = 0;
    let haut = Math.ceil(reste);
    while (net(haut) < reste) haut *= 2;

    while (haut - bas > 1) {
        const milieu = Math.floor((bas + haut) / 2);
        if (net(milieu) >= reste) haut = milieu;
        else bas = milieu;
    }
    return haut;
}

/**
 * Le tarif journalier qui laisse `netMensuel` chaque mois, frais payés.
 *
 * Rend `null` pour la vente de marchandises, qui ne se facture pas au temps.
 *
 * Le tarif s'entend hors taxe : sous la franchise en base, il n'y a pas de TVA,
 * et au-dessus elle s'ajoute à la facture sans rien changer à ce que vous
 * gardez. `tva.statut` dit laquelle des deux situations correspond au chiffre
 * d'affaires trouvé, sur une année pleine qui se répète.
 */
export function tarifJournalier({
    activite,
    artisan = false,
    optionFiscale = "bareme",
    autresRevenus = 0,
    enCouple = false,
    enfants = 0,
    parentIsole = false,
    netMensuel,
    fraisMensuels = 0,
    joursParSemaine,
    semainesConges,
    annee,
}) {
    if (!ACTIVITES_AU_TEMPS.includes(activite)) return null;

    const parametres = { activite, artisan, optionFiscale, autresRevenus, enCouple, enfants, parentIsole };
    const frais = fraisMensuels * 12;
    const chiffreAffaires = chiffreAffairesPour(netMensuel * 12 + frais, parametres);
    const detail = revenuNet({ ...parametres, chiffreAffaires });
    const jours = joursFactures({ joursParSemaine, semainesConges, annee });

    const plafond = PLAFONDS[activite];
    const auPlafond = revenuNet({ ...parametres, chiffreAffaires: plafond });
    const seuils = SEUILS_TVA.services;

    return {
        chiffreAffaires,
        jours,
        // Arrondi à l'euro supérieur : un tarif arrondi en dessous
        // manquerait l'objectif de quelques euros sur l'année.
        tarifJour: jours > 0 ? Math.ceil(chiffreAffaires / jours) : null,
        cotisations: detail.cotisations,
        cfp: detail.cfp,
        impot: detail.impot,
        frais,
        net: detail.net - frais,
        plafond,
        depassePlafond: chiffreAffaires > plafond,
        // Le plus que la micro puisse laisser chaque mois, frais payés : ce que
        // donne le plafond lui-même.
        netMensuelMaximum: Math.max(0, (auPlafond.net - frais) / 12),
        tva: {
            base: seuils.base,
            majore: seuils.majore,
            // Au-delà du seuil majoré, la TVA est due dès le jour du
            // dépassement ; entre les deux seuils, au 1er janvier suivant.
            statut:
                chiffreAffaires > seuils.majore
                    ? "immediate"
                    : chiffreAffaires > seuils.base
                      ? "janvier"
                      : "franchise",
        },
        taxeChambreDue: detail.taxeChambreDue,
        joursFeries: joursFeriesEnSemaine(annee),
    };
}
